import { Component, type ErrorInfo, type ReactNode } from 'react';

import appInsights from '@eneco/common/application-insights';

interface Props {
  children: ReactNode;
  componentName: string;
  /**
   * Rendered in place of the component when it throws. When omitted, a visible
   * marker is shown in non-production (to aid debugging in the CMS editor and
   * test/acceptance) and `null` is rendered in production.
   *
   * Supply this to render a branded fallback (e.g. a Sparky `NotificationBox`)
   * from a layer that can import the design system — `@eneco/cms` itself cannot.
   */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Per-component error boundary applied by `Placeholder` around every rendered
 * component. On error the exception is forwarded to Application Insights and the
 * component is replaced by `fallback` (see prop docs) so one broken component
 * never takes down the whole page.
 */
export class ComponentErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    appInsights.trackException({
      exception: error,
      properties: { componentName: this.props.componentName, ...errorInfo },
    });
  }

  override render() {
    const { hasError } = this.state;
    const { children, componentName, fallback } = this.props;

    if (!hasError) return children;

    if (fallback !== undefined) return fallback;

    // No fallback supplied: surface a visible marker in non-production so a
    // crashed component is obvious in the editor instead of silently vanishing.
    // Mirrors the `[missing component]` marker in `Placeholder`.
    if (process.env.NODE_ENV !== 'production') {
      return <div data-cms-error={componentName}>[component error: {componentName}]</div>;
    }

    return null;
  }
}
